// Arrow keys over the tiles view.
//
// The tiles are a justified layout, so "the tile above" is not "the same index in the previous
// row": rows differ in how many tiles they hold and how wide each is. The hook reads the tiles'
// on-screen rectangles and picks the tile in the next row that sits over the same column of the
// screen, the way the eye would. Left and right simply follow reading order, and wrap across
// rows, because that is the order the frames were made in.

import { useEffect, useRef } from "react";
import type { RefObject } from "react";

/** The attribute a tile carries so the hook can map a rectangle back to its frame. */
export const TILE_ID_ATTRIBUTE = "data-generation-id";

const ARROW_KEYS = new Set(["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"]);

/** Two tiles closer than this are on one row; flex rows share a top edge exactly, so 1px is slack. */
const ROW_TOLERANCE = 1;

interface TileRect {
  id: string;
  element: HTMLElement;
  left: number;
  right: number;
  top: number;
  bottom: number;
  centerX: number;
}

interface TileNavigationOptions {
  /** Off unless the board is showing tiles; the stream has no grid to navigate. */
  enabled: boolean;
  /** The scroll container holding the tiles. */
  containerRef: RefObject<HTMLElement | null>;
  /** Frame ids in stream order. Used only for the first pick, when nothing is selected. */
  orderedIds: string[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}

export function useTileNavigation({
  enabled,
  containerRef,
  orderedIds,
  selectedId,
  onSelect,
}: TileNavigationOptions): void {
  // Vertical moves keep a goal column, as a text editor keeps a goal column when the cursor
  // passes a short line: up through a narrow tile and down again returns to where you started,
  // rather than drifting toward whichever tile was widest. Any other change of selection drops it.
  const goal = useRef<{ fromId: string; x: number } | null>(null);

  // Read through refs so the listener is attached once, not on every render.
  const latest = useRef({ orderedIds, selectedId, onSelect });
  latest.current = { orderedIds, selectedId, onSelect };

  useEffect(() => {
    if (!enabled) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (!ARROW_KEYS.has(event.key)) return;
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
      if (isEditing(event.target)) return;

      const container = containerRef.current;
      const { orderedIds, selectedId, onSelect } = latest.current;
      if (!container || orderedIds.length === 0) return;

      const tiles = readTiles(container);
      let target: TileRect | undefined;

      if (selectedId === null || !tiles.some((tile) => tile.id === selectedId)) {
        // The pane is closed: any arrow opens it on the first frame.
        target = tiles.find((tile) => tile.id === orderedIds[0]) ?? tiles[0];
        goal.current = null;
      } else if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
        target = horizontal(tiles, selectedId, event.key === "ArrowRight" ? 1 : -1);
        goal.current = null;
      } else {
        const x = goal.current?.fromId === selectedId ? goal.current.x : undefined;
        const found = vertical(tiles, selectedId, x, event.key === "ArrowDown" ? 1 : -1);
        if (found) {
          target = found.tile;
          goal.current = { fromId: found.tile.id, x: found.x };
        }
      }

      // The key is ours even when there is nowhere to go, or the stream would scroll instead.
      event.preventDefault();
      if (!target || target.id === selectedId) return;

      onSelect(target.id);
      target.element.scrollIntoView({
        block: "nearest",
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
      });
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [enabled, containerRef]);
}

// ── Geometry ───────────────────────────────────────────────────────────────────────────────

function readTiles(container: HTMLElement): TileRect[] {
  const elements = container.querySelectorAll<HTMLElement>(`[${TILE_ID_ATTRIBUTE}]`);
  const tiles: TileRect[] = [];
  for (const element of elements) {
    const id = element.getAttribute(TILE_ID_ATTRIBUTE);
    if (!id) continue;
    const rect = element.getBoundingClientRect();
    tiles.push({
      id,
      element,
      left: rect.left,
      right: rect.right,
      top: rect.top,
      bottom: rect.bottom,
      centerX: rect.left + rect.width / 2,
    });
  }
  return tiles;
}

/**
 * The next frame in reading order. A frame with several images owns several adjacent tiles, so
 * the step is to the first tile past the current frame's run, not the next tile.
 */
function horizontal(tiles: TileRect[], currentId: string, direction: 1 | -1): TileRect | undefined {
  const first = tiles.findIndex((tile) => tile.id === currentId);
  let last = first;
  while (last + 1 < tiles.length && tiles[last + 1].id === currentId) last += 1;

  return direction === 1 ? tiles[last + 1] : tiles[first - 1];
}

/**
 * The tile in the adjacent row that lies under the goal column. Preference goes to a tile that
 * spans the column outright; failing that, the one whose edge is nearest to it.
 */
function vertical(
  tiles: TileRect[],
  currentId: string,
  goalX: number | undefined,
  direction: 1 | -1,
): { tile: TileRect; x: number } | undefined {
  const own = tiles.filter((tile) => tile.id === currentId);
  if (own.length === 0) return undefined;

  // With a goal column, start from whichever of the frame's tiles is nearest it.
  const from =
    goalX === undefined
      ? own[0]
      : own.reduce((best, tile) => (distance(tile, goalX) < distance(best, goalX) ? tile : best));
  const x = goalX ?? from.centerX;

  const beyond =
    direction === 1
      ? tiles.filter((tile) => tile.top >= from.bottom - ROW_TOLERANCE && tile.id !== currentId)
      : tiles.filter((tile) => tile.bottom <= from.top + ROW_TOLERANCE && tile.id !== currentId);
  if (beyond.length === 0) return undefined;

  const edge = direction === 1 ? Math.min(...beyond.map((tile) => tile.top)) : Math.max(...beyond.map((tile) => tile.top));
  const row = beyond.filter((tile) => Math.abs(tile.top - edge) <= ROW_TOLERANCE);

  const tile = row.reduce((best, candidate) =>
    distance(candidate, x) < distance(best, x) ? candidate : best,
  );
  return { tile, x };
}

/** Zero when the column passes through the tile, otherwise the gap to its nearest edge. */
function distance(tile: TileRect, x: number): number {
  if (x < tile.left) return tile.left - x;
  if (x > tile.right) return x - tile.right;
  return 0;
}

/** Arrow keys inside a field, a select or a slider belong to that control. */
function isEditing(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  return target.closest("input, textarea, select, [contenteditable]:not([contenteditable='false'])") !== null;
}
